import useBrandList from 'commons/hooks/use-brand-list';
import LOCALS from 'commons/locals';
import {
  bodyListDefault,
  bottomListDefault,
  brightListDefault,
  handrailListDefault,
  insideListDefault,
  metalListDefault,
  othersListDefault,
  shoulderStrapListDefault,
} from 'components/product-add-edit/utils';
import { LANGUAGE_MAP } from 'constant/language';
import { ProductTaxRegime } from 'constants/france-tax';
import i18n from 'i18n';
import { useCallback, useMemo } from 'react';
import { Control, useWatch } from 'react-hook-form';
import { useAppSelector } from 'store/hooks';
import {
  selectGlobalInfo,
  SelectOptionLabelList,
} from 'store/slices/globalInfoSlice';
import { CascaderOption } from 'types/base';
import findCascaderOptionById from 'utils/findCascaderOptionById';
import { z } from 'zod';

export const createProductFormSchema = z.object({
  owner: z.string().optional(),
  productCategoryIdList: z.array(z.number()).nonempty(),
  boxId: z.number().nullable().optional(),
  extendType: z.string().optional().nullable(),
  stockPlace: z.string(),
  brandName: z.string().nonempty(),
  currency: z.string().nonempty(),
  price: z.number().min(0),
  priceWithoutTax: z.number().min(0),
  costPrice: z.number().min(0),
  careCost: z.number().min(0).nullable().optional(),
  referencePrice: z.number().nullable(),
  skuType: z.number(),
  stock: z.number().min(0),
  sourceType: z.number(),
  promotionType: z.number(),
  originalPrice: z.number().nullable(),
  description: z.string().optional(),
  remark: z.string().optional(),
  rank: z.string(),
  colors: z.string().optional(),
  color: z.array(z.string().nonempty()).optional(),
  colorRemark: z.string().optional(),
  material: z.array(z.array(z.string().nonempty())).optional(),
  materialRemark: z.string().optional(),
  hardware: z.array(z.string().nonempty()).optional(),
  hardwareRemark: z.string().optional(),
  stamp: z.string().optional(),
  stampRemark: z.string().optional(),
  fullStamp: z.string().optional(),
  accessory: z.array(z.string().nonempty()).optional(),
  collections: z.array(z.string().nonempty()).optional(),
  facebookEnabled: z.number(),
  albumPics: z.array(z.string().nonempty()).optional(),
  internalImages: z.array(z.string().nonempty()),
  name: z.object({
    en: z.string().nonempty(),
    ja: z.string().nonempty(),
    zh_CN: z.string().nonempty(),
    zh_TW: z.string().nonempty(),
  }),
  size: z.object({
    en: z.string().optional(),
    ja: z.string().optional(),
    zh_CN: z.string().optional(),
    zh_TW: z.string().optional(),
  }),
  /**
   * 列表标题, 由商品名称和商品颜色组成自动生成
   * 商品名称又会被商品分类影响
   * 所以在商品分类、商品名称、商品颜色的 onChange 时需要自动生成
   */
  subTitle: z.object({
    en: z.string().optional(),
    ja: z.string().optional(),
    zh_CN: z.string().optional(),
    zh_TW: z.string().optional(),
  }),
  /**
   * 列表小标题, 有商品材质和金属件组成自动生成
   * 商品材质又会被商品分类影响
   * 所以在商品分类、商品材质、金属件的 onChange 时需要自动生成
   */
  note: z.object({
    en: z.string().optional(),
    ja: z.string().optional(),
    zh_CN: z.string().optional(),
    zh_TW: z.string().optional(),
  }),
  /**
   * 详细页标题，由下面这个选项自动生成
   * 1. 品牌名称
   * 2. 等级
   * 3. 合集（特殊合集）
   * 4. 商品名称
   * 5. 商品颜色
   * 6. 商品材质
   * 7. 金属件
   * 8. 印章
   *
   * 其中
   * 合集、商品名称、商品材质，又会受到商品分类的影响
   * 所以在商品分类发生变化时，也需要自动生成
   */
  detailTitle: z.object({
    en: z.string().optional(),
    ja: z.string().optional(),
    zh_CN: z.string().optional(),
    zh_TW: z.string().optional(),
  }),
  /**
   * 等级描述
   */
  rankDesc: z.object({
    en: z.string().optional(),
    ja: z.string().optional(),
    zh_CN: z.string().optional(),
    zh_TW: z.string().optional(),
  }),

  /**
   * 等级描述，当前选中的快捷选项，不需要上传后端，要存到表单里，主要是需要在 rank 组件中访问
   */
  selectedRankDescOptions: z.record(z.string(), z.array(z.number())),

  // 商品港币价格，目前只在库存地为上海时使用
  priceHkd: z.number().nullable().optional(),
  /** 商品税制：STANDARD_TVA | MARGIN（仅法国库存地） */
  // 不用 z.enum：TS 4.9 无法解析 Zod4 的 const 泛型签名
  taxRegime: z
    .custom<ProductTaxRegime>(
      (val) =>
        val === ProductTaxRegime.STANDARD_TVA ||
        val === ProductTaxRegime.MARGIN,
    )
    .nullable()
    .optional(),
});

export type CreateProductFormSchemaType = z.infer<
  typeof createProductFormSchema
>;

export const LANG_LIST = [
  LANGUAGE_MAP.JA,
  LANGUAGE_MAP.EN,
  LANGUAGE_MAP.ZH_CN,
  LANGUAGE_MAP.ZH_TW,
] as const;

export const generateSubTitle = (
  name: CreateProductFormSchemaType['name'],
  color: CreateProductFormSchemaType['color'],
  colorSelectOptions: SelectOptionLabelList,
) => {
  const subTitleEnArr = [name.en];
  const subTitleJaArr = [name.ja];
  const subTitleZhCnArr = [name.zh_CN];
  const subTitleZhTwArr = [name.zh_TW];

  if (color && color.length) {
    color.forEach((color) => {
      const colorOption = colorSelectOptions.find(
        (option) => option.value === color,
      );
      if (colorOption) {
        subTitleEnArr.push(colorOption.labelEn || '');
        subTitleJaArr.push(colorOption.labelJa || '');
        subTitleZhCnArr.push(colorOption.labelCn || '');
        subTitleZhTwArr.push(colorOption.labelTw || '');
      }
    });
  }

  return {
    en: subTitleEnArr.filter(Boolean).join(' '),
    ja: subTitleJaArr.filter(Boolean).join(' '),
    zh_CN: subTitleZhCnArr.filter(Boolean).join(' '),
    zh_TW: subTitleZhTwArr.filter(Boolean).join(' '),
  };
};

export const generateNote = (
  material: CreateProductFormSchemaType['material'],
  hardware: CreateProductFormSchemaType['hardware'],
  materialCascaderOptions: CascaderOption[],
  hardwareSelectOptions: SelectOptionLabelList,
) => {
  const noteEnArr: string[] = [];
  const noteJaArr: string[] = [];
  const noteZhCnArr: string[] = [];
  const noteZhTwArr: string[] = [];

  if (material && material.length) {
    material.forEach((material) => {
      const materialOption = findCascaderOptionById(
        material[material.length - 1],
        materialCascaderOptions,
      );

      if (materialOption) {
        noteEnArr.push(materialOption.labelEn || '');
        noteJaArr.push(materialOption.labelJa || '');
        noteZhCnArr.push(materialOption.labelCn || '');
        noteZhTwArr.push(materialOption.labelTw || '');
      }
    });
  }

  if (hardware && hardware.length) {
    hardware.forEach((hardware) => {
      const hardwareOption = hardwareSelectOptions.find(
        (option) => option.value === hardware && option.value !== 'none',
      );

      if (hardwareOption) {
        noteEnArr.push(hardwareOption.labelEn || '');
        noteJaArr.push(hardwareOption.labelJa || '');
        noteZhCnArr.push(hardwareOption.labelCn || '');
        noteZhTwArr.push(hardwareOption.labelTw || '');
      }
    });
  }

  return {
    en: noteEnArr.filter(Boolean).join(' '),
    ja: noteJaArr.filter(Boolean).join(' '),
    zh_CN: noteZhCnArr.filter(Boolean).join(' '),
    zh_TW: noteZhTwArr.filter(Boolean).join(' '),
  };
};

export const useMaterialCascaderOptionsByFirstLevelCategoryId = (
  firstLevelId?: number,
) => {
  const { materialCascaderOptionsMap } = useAppSelector(selectGlobalInfo);

  return useMemo(() => {
    if (!firstLevelId)
      return {
        firstLevelId,
        materialCascaderOptions: [],
      };

    if (!Object.keys(materialCascaderOptionsMap).length)
      return {
        firstLevelId,
        materialCascaderOptions: [],
      };

    return {
      firstLevelId,
      materialCascaderOptions: [
        ...materialCascaderOptionsMap[firstLevelId],
      ].sort((a, b) => a.sort! - b.sort!),
    };
  }, [firstLevelId, materialCascaderOptionsMap]);
};

export const useMaterialCascaderOptions = (
  control: Control<CreateProductFormSchemaType>,
) => {
  const productCategoryIdList = useWatch({
    control,
    name: 'productCategoryIdList',
  });
  const firstLevelId = productCategoryIdList[0];

  return useMaterialCascaderOptionsByFirstLevelCategoryId(firstLevelId);
};

export const useCollectionSelectOptions = (
  control: Control<CreateProductFormSchemaType>,
) => {
  const { collectionSelectOptionsMap } = useAppSelector(selectGlobalInfo);
  const productCategoryIdList = useWatch({
    control,
    name: 'productCategoryIdList',
  });
  const productCategoryIdFirstLevel = productCategoryIdList[0];

  const collectionSelectOptions = useMemo(() => {
    if (!productCategoryIdFirstLevel) return [];
    if (!Object.keys(collectionSelectOptionsMap).length) return [];

    return collectionSelectOptionsMap[productCategoryIdFirstLevel] || [];
  }, [collectionSelectOptionsMap, productCategoryIdFirstLevel]);

  return {
    collectionSelectOptions,
  };
};

// 需要拼接到 detailTitle 的特殊处理的合集列表
const SPECIAL_COLLECTIONS_LIST = ['Special Order', 'Limited Edition'];
export const useGenerateDetailTitle = (
  control: Control<CreateProductFormSchemaType>,
) => {
  const brandNameValue = useWatch({
    control,
    name: 'brandName',
  });
  const rankValue = useWatch({
    control,
    name: 'rank',
  });
  const collectionsValue = useWatch({
    control,
    name: 'collections',
  });
  const nameValue = useWatch({
    control,
    name: 'name',
  });
  const colorValue = useWatch({
    control,
    name: 'color',
  });
  const materialValue = useWatch({
    control,
    name: 'material',
  });
  const hardwareValue = useWatch({
    control,
    name: 'hardware',
  });
  const stampValue = useWatch({
    control,
    name: 'stamp',
  });

  const {
    rankSelectOptions,
    colorSelectOptions,
    hardwareSelectOptions,
    stampSelectOptions,
  } = useAppSelector(selectGlobalInfo);
  const { BrandList: brandOptionList } = useBrandList();
  const { collectionSelectOptions } = useCollectionSelectOptions(control);
  const { materialCascaderOptions } = useMaterialCascaderOptions(control);

  const generateDetailTitle = useCallback(
    ({
      brandName = brandNameValue,
      rank = rankValue,
      collections = collectionsValue,
      name = nameValue,
      color = colorValue,
      material = materialValue,
      hardware = hardwareValue,
      stamp = stampValue,
    }: {
      brandName?: CreateProductFormSchemaType['brandName'];
      rank?: CreateProductFormSchemaType['rank'];
      collections?: CreateProductFormSchemaType['collections'];
      name?: CreateProductFormSchemaType['name'];
      color?: CreateProductFormSchemaType['color'];
      material?: CreateProductFormSchemaType['material'];
      hardware?: CreateProductFormSchemaType['hardware'];
      stamp?: CreateProductFormSchemaType['stamp'];
    }) => {
      const detailTitleEnArr: string[] = [];
      const detailTitleJaArr: string[] = [];
      const detailTitleZhCnArr: string[] = [];
      const detailTitleZhTwArr: string[] = [];

      const rankOption = rankSelectOptions.find((d) => d.value === rank);
      if (rankOption) {
        detailTitleEnArr.push(rankOption.labelEn || '');
        detailTitleJaArr.push(rankOption.labelJa || '');
        detailTitleZhCnArr.push(rankOption.labelCn || '');
        detailTitleZhTwArr.push(rankOption.labelTw || '');

        const brandOption = brandOptionList.find((d) => d.value === brandName);
        if (brandOption) {
          detailTitleEnArr.push(brandOption.descEn || '');
          detailTitleJaArr.push(brandOption.descJa || '');
          detailTitleZhCnArr.push(brandOption.descZh || '');
          detailTitleZhTwArr.push(brandOption.descZhTw || '');
        }
      }

      if (collections && collections.length) {
        SPECIAL_COLLECTIONS_LIST.forEach((collection) => {
          if (collections.includes(collection)) {
            const collectionOption = collectionSelectOptions.find(
              (d) => d.value === collection,
            );
            if (collectionOption) {
              detailTitleEnArr.push(collectionOption.labelEn || '');
              detailTitleJaArr.push(collectionOption.labelJa || '');
              detailTitleZhCnArr.push(collectionOption.labelCn || '');
              detailTitleZhTwArr.push(collectionOption.labelTw || '');
            }
          }
        });
      }

      if (name) {
        detailTitleEnArr.push(name.en || '');
        detailTitleJaArr.push(name.ja || '');
        detailTitleZhCnArr.push(name.zh_CN || '');
        detailTitleZhTwArr.push(name.zh_TW || '');
      }

      if (color && color.length) {
        color.forEach((color) => {
          const colorOption = colorSelectOptions.find((d) => d.value === color);
          if (colorOption) {
            detailTitleEnArr.push(colorOption.labelEn || '');
            detailTitleJaArr.push(colorOption.labelJa || '');
            detailTitleZhCnArr.push(colorOption.labelCn || '');
            detailTitleZhTwArr.push(colorOption.labelTw || '');
          }
        });
      }

      if (material && material.length) {
        material.forEach((material) => {
          const materialOption = findCascaderOptionById(
            material[material.length - 1],
            materialCascaderOptions,
          );
          if (materialOption) {
            detailTitleEnArr.push(materialOption.labelEn || '');
            detailTitleJaArr.push(materialOption.labelJa || '');
            detailTitleZhCnArr.push(materialOption.labelCn || '');
            detailTitleZhTwArr.push(materialOption.labelTw || '');
          }
        });
      }

      if (hardware && hardware.length) {
        hardware.forEach((hardware) => {
          const hardwareOption = hardwareSelectOptions.find(
            (d) => d.value === hardware && d.value !== 'none',
          );
          if (hardwareOption) {
            detailTitleEnArr.push(hardwareOption.labelEn || '');
            detailTitleJaArr.push(hardwareOption.labelJa || '');
            detailTitleZhCnArr.push(hardwareOption.labelCn || '');
            detailTitleZhTwArr.push(hardwareOption.labelTw || '');
          }
        });
      }

      if (stamp) {
        const stampOption = stampSelectOptions.find(
          (d) => d.value === stamp && d.value !== 'none',
        );
        if (stampOption) {
          detailTitleEnArr.push(stampOption.labelEn || '');
          detailTitleJaArr.push(stampOption.labelJa || '');
          detailTitleZhCnArr.push(stampOption.labelCn || '');
          detailTitleZhTwArr.push(stampOption.labelTw || '');
        }
      }

      return {
        en: detailTitleEnArr
          .map((d) => d.trim())
          .filter(Boolean)
          .join(' '),
        ja: detailTitleJaArr
          .map((d) => d.trim())
          .filter(Boolean)
          .join(' '),
        zh_CN: detailTitleZhCnArr
          .map((d) => d.trim())
          .filter(Boolean)
          .join(' '),
        zh_TW: detailTitleZhTwArr
          .map((d) => d.trim())
          .filter(Boolean)
          .join(' '),
      };
    },
    [
      brandNameValue,
      brandOptionList,
      collectionSelectOptions,
      collectionsValue,
      colorSelectOptions,
      colorValue,
      hardwareSelectOptions,
      hardwareValue,
      materialCascaderOptions,
      materialValue,
      nameValue,
      rankSelectOptions,
      rankValue,
      stampSelectOptions,
      stampValue,
    ],
  );

  return {
    generateDetailTitle,
  };
};

export const RAND_DESC_OPTION_LIST = [
  {
    title: i18n.t(LOCALS.Hardware),
    titleEn: i18n.getFixedT('en')(LOCALS.Hardware),
    titleJa: i18n.getFixedT('ja')(LOCALS.Hardware),
    titleZhCn: i18n.getFixedT('zh_CN')(LOCALS.Hardware),
    titleZhTw: i18n.getFixedT('zh_TW')(LOCALS.Hardware),
    options: metalListDefault(),
    key: 'hardware',
  },
  {
    title: i18n.t(LOCALS.Edges),
    titleEn: i18n.getFixedT('en')(LOCALS.Edges),
    titleJa: i18n.getFixedT('ja')(LOCALS.Edges),
    titleZhCn: i18n.getFixedT('zh_CN')(LOCALS.Edges),
    titleZhTw: i18n.getFixedT('zh_TW')(LOCALS.Edges),
    options: bottomListDefault(),
    key: 'edges',
  },
  {
    title: i18n.t(LOCALS.Outer),
    titleEn: i18n.getFixedT('en')(LOCALS.Outer),
    titleJa: i18n.getFixedT('ja')(LOCALS.Outer),
    titleZhCn: i18n.getFixedT('zh_CN')(LOCALS.Outer),
    titleZhTw: i18n.getFixedT('zh_TW')(LOCALS.Outer),
    options: bodyListDefault(),
    key: 'outer',
  },
  {
    title: i18n.t(LOCALS.Inner),
    titleEn: i18n.getFixedT('en')(LOCALS.Inner),
    titleJa: i18n.getFixedT('ja')(LOCALS.Inner),
    titleZhCn: i18n.getFixedT('zh_CN')(LOCALS.Inner),
    titleZhTw: i18n.getFixedT('zh_TW')(LOCALS.Inner),
    options: insideListDefault(),
    key: 'inner',
  },
  {
    title: i18n.t(LOCALS.Handle),
    titleEn: i18n.getFixedT('en')(LOCALS.Handle),
    titleJa: i18n.getFixedT('ja')(LOCALS.Handle),
    titleZhCn: i18n.getFixedT('zh_CN')(LOCALS.Handle),
    titleZhTw: i18n.getFixedT('zh_TW')(LOCALS.Handle),
    options: handrailListDefault(),
    key: 'handle',
  },
  {
    title: i18n.t(LOCALS.shoulder_strap),
    titleEn: i18n.getFixedT('en')(LOCALS.shoulder_strap),
    titleJa: i18n.getFixedT('ja')(LOCALS.shoulder_strap),
    titleZhCn: i18n.getFixedT('zh_CN')(LOCALS.shoulder_strap),
    titleZhTw: i18n.getFixedT('zh_TW')(LOCALS.shoulder_strap),
    options: shoulderStrapListDefault(),
    key: 'shoulder_strap',
  },
  {
    title: i18n.t(LOCALS.Highlights),
    titleEn: i18n.getFixedT('en')(LOCALS.Highlights),
    titleJa: i18n.getFixedT('ja')(LOCALS.Highlights),
    titleZhCn: i18n.getFixedT('zh_CN')(LOCALS.Highlights),
    titleZhTw: i18n.getFixedT('zh_TW')(LOCALS.Highlights),
    options: brightListDefault(),
    key: 'highlights',
  },
  {
    title: i18n.t(LOCALS.others),
    titleEn: i18n.getFixedT('en')(LOCALS.others),
    titleJa: i18n.getFixedT('ja')(LOCALS.others),
    titleZhCn: i18n.getFixedT('zh_CN')(LOCALS.others),
    titleZhTw: i18n.getFixedT('zh_TW')(LOCALS.others),
    options: othersListDefault(),
    key: 'others',
  },
];

export const generateInitSelectedRankDescOptions = () => {
  const out: {
    [key: string]: number[];
  } = {};
  RAND_DESC_OPTION_LIST.forEach((item) => {
    out[item.key] = [];
  });

  return out;
};

export const generateRankDesc = ({
  selectedRankDescOptions,
  rank,
  rankSelectOptions,
}: {
  selectedRankDescOptions: {
    [key: string]: number[];
  };
  rank: CreateProductFormSchemaType['rank'];
  rankSelectOptions: SelectOptionLabelList;
}) => {
  let rankStrEn = '';
  let rankStrJa = '';
  let rankStrZhCn = '';
  let rankStrZhTw = '';
  const rankOption = rankSelectOptions.find((d) => d.value === rank);
  if (rankOption) {
    rankStrEn = rankOption.labelEn || '';
    rankStrJa = rankOption.labelJa || '';
    rankStrZhCn = rankOption.labelCn || '';
    rankStrZhTw = rankOption.labelTw || '';
  }

  const rankDescEnArr: string[][] = [];
  const rankDescJaArr: string[][] = [];
  const rankDescZhCnArr: string[][] = [];
  const rankDescZhTwArr: string[][] = [];

  RAND_DESC_OPTION_LIST.forEach((item) => {
    const selectOptions = selectedRankDescOptions[item.key];
    if (selectOptions && selectOptions.length) {
      const arrEn = [];
      const arrJa = [];
      const arrZhCn = [];
      const arrZhTw = [];

      arrEn.push(`[${item.titleEn}]`);
      arrJa.push(`[${item.titleJa}]`);
      arrZhCn.push(`[${item.titleZhCn}]`);
      arrZhTw.push(`[${item.titleZhTw}]`);

      selectOptions.forEach((option) => {
        arrEn.push(item.options[option].en || '');
        arrJa.push(item.options[option].ja || '');
        arrZhCn.push(item.options[option].zh || '');
        arrZhTw.push(item.options[option].zh_TW || '');
      });

      rankDescEnArr.push(arrEn);
      rankDescJaArr.push(arrJa);
      rankDescZhCnArr.push(arrZhCn);
      rankDescZhTwArr.push(arrZhTw);
    }
  });

  const rankSelectEn = rankDescEnArr
    .map((d) =>
      d
        .map((d) => d.trim())
        .filter(Boolean)
        .join('\n'),
    )
    .filter(Boolean)
    .join('\n\n');
  const rankSelectJa = rankDescJaArr
    .map((d) =>
      d
        .map((d) => d.trim())
        .filter(Boolean)
        .join('\n'),
    )
    .filter(Boolean)
    .join('\n\n');
  const rankSelectZhCn = rankDescZhCnArr
    .map((d) =>
      d
        .map((d) => d.trim())
        .filter(Boolean)
        .join('\n'),
    )
    .filter(Boolean)
    .join('\n\n');
  const rankSelectZhTw = rankDescZhTwArr
    .map((d) =>
      d
        .map((d) => d.trim())
        .filter(Boolean)
        .join('\n'),
    )
    .filter(Boolean)
    .join('\n\n');

  return {
    en: [rankStrEn, rankSelectEn].filter(Boolean).join('\n'),
    ja: [rankStrJa, rankSelectJa].filter(Boolean).join('\n'),
    zh_CN: [rankStrZhCn, rankSelectZhCn].filter(Boolean).join('\n'),
    zh_TW: [rankStrZhTw, rankSelectZhTw].filter(Boolean).join('\n'),
  };
};
